import React from "react";
import { PendingOrder, PrevInstruction, StrategyFilter } from "./types";
import { SuggestionMap, hasViewAtHorizon } from "./recommendationSelectors";

const fmtCache = new Map<string, Intl.NumberFormat>();
// minDecimals defaults to decimals, so digits are pinned unless a caller asks for a
// range — pass 0 for "up to N places", which trims trailing zeros.
export const fmtNum = (v: number | string | null | undefined, decimals = 2, minDecimals = decimals): string => {
  if (v == null || v === "") return "—";
  const n = typeof v === "string" ? parseFloat(v.replace(/,/g, "")) : v;
  if (isNaN(n)) return "—";
  const cacheKey = `${decimals}:${minDecimals}`;
  if (!fmtCache.has(cacheKey)) {
    fmtCache.set(cacheKey, new Intl.NumberFormat("en-IN", { minimumFractionDigits: minDecimals, maximumFractionDigits: decimals }));
  }
  return fmtCache.get(cacheKey)!.format(parseFloat(n.toFixed(decimals)));
};

// Domestic/Global price formatting lives in the shared formatter; re-exported here so
// existing `from "../utils"` imports keep working.
export { formatPrice } from "@/utils/formatters";

/** A bound price formatter (value → display string). */
export type PriceFmt = (value: number | string | null | undefined) => string;

/** Default price formatter used when a row supplies none: Domestic "₹" style. */
export const DEFAULT_PRICE_FMT: PriceFmt = (value) => `₹${fmtNum(value)}`;

export const LHF_CODES = new Set(["LHF", "LHF_AIF"]);
// "All Orders" is a pending-order-only switch, not a company category: while
// selected (it is on by default), every real tracker row shows regardless of
// company category. Recommendation rows ignore it and always follow the
// letter/Invested chips. Deselect it to narrow pending orders by category too.
export const ALL_ORDERS_CATEGORY = "All Orders";
export const CATEGORY_OPTIONS = [ALL_ORDERS_CATEGORY, "I", "A", "B", "B-", "C", "W0", "W1", "W2", "W3", "E", "H", "R"];
export const DEFAULT_CATEGORIES = [ALL_ORDERS_CATEGORY, "I", "A", "B", "B-", "C"];
// Global defaults additionally include the W1/W2/W3 watchlist buckets, which are part of
// the day-to-day global universe rather than a narrowing filter. Domestic keeps the
// shorter default; each country tab holds its own category selection.
export const GLOBAL_DEFAULT_CATEGORIES = [...DEFAULT_CATEGORIES, "W1", "W2", "W3"];

/** Default category chips for a country tab. Global adds W1/W2/W3; Domestic does not. */
export const defaultCategoriesFor = (isGlobal: boolean): string[] =>
  isGlobal ? GLOBAL_DEFAULT_CATEGORIES : DEFAULT_CATEGORIES;

export const matchesStrategyGroup = (order: PendingOrder, filter: StrategyFilter): boolean => {
  if (filter === "all") return true;
  if (order.is_oms_rec) {
    return filter === "lhf" ? hasViewAtHorizon(order.recommendations, 5) : hasViewAtHorizon(order.recommendations, 3);
  }
  const isLhf = order.strategy_group === "LHF" || LHF_CODES.has(order.strategy_code || "");
  return filter === "lhf" ? isLhf : !isLhf;
};


export const getIrClass = (value: number | null | undefined): string => {
  if (value == null) return "";
  return value > 0 ? "ir-positive" : value < 0 ? "ir-negative" : "";
};

/** Shared Model IR tooltip — buy-below takes priority; falls back to decision-labelled target.
 *  Renders the model's own strategy suggestion picked by decision side and horizon. */
export const buildModelIrTooltip = (
  modelDecision: string | null | undefined,
  buyBelow: number | null | undefined,
  targetPrice: number | null | undefined,
  targetDate: string | null | undefined,
  latestPrice: number | null | undefined,
  suggestion?: SuggestionMap | null,
  // Formatters for the rec price (raw currency) and the CMP (LTP currency).
  // Defaults keep Domestic rows as "₹1,234.50".
  fmtRec: PriceFmt = DEFAULT_PRICE_FMT,
  fmtCmp: PriceFmt = DEFAULT_PRICE_FMT,
  // Global rows show only the SGS suggestion; Domestic rows hide SGS.
  isGlobal = false,
): React.ReactNode => {
  const suggestionEntries = Object.entries(suggestion ?? {})
    .filter(([code, s]) => s?.trade_type != null && (isGlobal ? code === "SGS" : code !== "SGS"));
  if (buyBelow == null && targetPrice == null && latestPrice == null && suggestionEntries.length === 0) return "";
  return (
    <span>
      {buyBelow != null ? (
        <>
          {modelDecision || "Decision"}<br />
          {`Rec. Price: ${fmtRec(buyBelow)}`}<br />
          {targetPrice != null && <>{`Target: ${fmtRec(targetPrice)}`}<br /></>}
        </>
      ) : targetPrice != null ? (
        <>
          {modelDecision
            ? <>{modelDecision}<br />{`Rec. Price: ${fmtRec(targetPrice)}`}</>
            : `Target: ${fmtRec(targetPrice)}`}
          <br />
        </>
      ) : null}
      {latestPrice != null && <>{`CMP: ${fmtCmp(latestPrice)}`}<br /></>}
      {targetDate && <>{`Target Date: ${targetDate}`}<br /></>}
      {suggestionEntries.length > 0 && (
        <>
          <span style={{ fontWeight: 600 }}>Model Suggestion:</span><br />
          {suggestionEntries.map(([code, s]) => (
            <React.Fragment key={code}>&nbsp;&nbsp;{code}: {s.trade_type}<br /></React.Fragment>
          ))}
        </>
      )}
    </span>
  );
};

/** Holdings stats tooltip — min/median/max per-client % */
export const buildHoldingStatsTooltip = (
  stats:
    | {
        min: number;
        median: number;
        max: number;
        accounts_below_target_with_cash?: number;
        cio_target_pct?: number | null;
        client_count?: number;
      }
    | null
    | undefined,
  fmtFn: (v: number | string | null | undefined) => string,
  currentHoldingPct?: number | null,
  showBuyable = true,
): React.ReactNode => {
  if (!stats) return "";

  const count = stats.accounts_below_target_with_cash ?? 0;
  // Only show the cash block when at least one client can still buy, matching the
  // tint logic in cashTintClass. `showBuyable` is false on sell rows, where a
  // "can buy" count is meaningless.
  const hasCashInfo = showBuyable && count > 0;
  const hasTarget = stats.cio_target_pct != null;
  const total = stats.client_count;

  // No CIO decision -> the target is the current holding %; show its value when we have it.
  const targetValue = hasTarget
    ? `CIO ${fmtFn(stats.cio_target_pct)}%`
    : currentHoldingPct != null
      ? `${fmtFn(currentHoldingPct)}% (holding)`
      : "group average holding %";

  const row = (label: string, value: string, key: string) => (
    <div key={key} style={{ display: "flex", justifyContent: "space-between", gap: 14 }}>
      <span style={{ opacity: 0.85 }}>{label}</span>
      <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div style={{ minWidth: 160 }}>
      {row("Min", `${fmtFn(stats.min)}%`, "min")}
      {row("Median", `${fmtFn(stats.median)}%`, "median")}
      {row("Max", `${fmtFn(stats.max)}%`, "max")}
      {hasCashInfo && (
        <>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.3)", margin: "5px 0" }} />
          {row("Target", targetValue, "tgt")}
          {row("Buyable clients", total != null ? `${count}/${total}` : `${count}`, "cnt")}
          <div style={{ opacity: 0.6, fontSize: "0.85em", marginTop: 4 }}>
            Cash ≥ ½ the gap to target; target gap 0.25%.
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Orange tint class for the PMS Holding% cell, bucketed by the count of clients that can still
 * buy (below target with enough cash): 1–5, 6–20, 20+. Colours live in the page palette
 * (--orec-cash-*). Undefined for 0 so the cell keeps its background.
 */
export const cashTintClass = (
  accountsBelowTargetWithCash: number | null | undefined,
): "cash-1" | "cash-2" | "cash-3" | undefined => {
  const count = accountsBelowTargetWithCash ?? 0;
  if (count <= 0) return undefined;
  if (count <= 5) return "cash-1";
  if (count <= 20) return "cash-2";
  return "cash-3";
};

export const defaultStartDate = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - 4);
  return d.toISOString().slice(0, 10);
};

export type OrderTypeFilter = "all" | "buy" | "sell";
export type PriorityFilter  = "all" | "high" | "medium" | "low";
export type MethodFilter    = "all" | "vwap" | "pct_volume" | "none";
export type DecisionFilter    = "all" | "continue" | "none";
export type LastOrderFilter = "all" | "none" | "has_warning" | "has_pending_codes";
export type MarketableFilter = "all" | "marketable";
export type RowTypeFilter   = "all" | "orders" | "recommendations";
export type FilterLevel     = "company" | "strategy";

/** A buy is non-marketable when its limit price is below CMP; a sell when its
 *  limit price is above CMP. Market-execution orders are always marketable
 *  (they execute at CMP). */
export const isMarketableOrder = (o: PendingOrder): boolean => {
  if (o.execution_type === "market") return true;
  const cmp = o.latest_price ?? 0;
  const limit = parseFloat(o.limit_price || "0");
  const isNonMarketable = o.order_type === 1 ? limit < cmp : limit > cmp;
  return !isNonMarketable;
};

/** True when any order-level (pending-tracker) filter is applied. Recommendations
 *  can never match these, so callers use this to hide/skip rec rows. */
export const anyOrderLevelFilterActive = (
  orderTypeFilter: OrderTypeFilter,
  priorityFilter: PriorityFilter,
  methodFilter: MethodFilter,
  decisionFilter: DecisionFilter,
  lastOrderFilter: LastOrderFilter,
  marketableFilter: MarketableFilter,
): boolean =>
  orderTypeFilter !== "all" ||
  priorityFilter !== "all" ||
  methodFilter !== "all" ||
  decisionFilter !== "all" ||
  lastOrderFilter !== "all" ||
  marketableFilter !== "all";

/** Execution-level checks for a real tracker row (assumes !is_oms_rec && !is_placeholder). */
export const matchesExecutionFilters = (
  o: PendingOrder,
  orderTypeFilter: OrderTypeFilter,
  priorityFilter: PriorityFilter,
  methodFilter: MethodFilter,
  decisionFilter: DecisionFilter,
  marketableFilter: MarketableFilter,
): boolean => {
  if (orderTypeFilter !== "all" && (o.order_type === 1 ? "buy" : "sell") !== orderTypeFilter) return false;
  if (priorityFilter !== "all" && (o.priority ?? "low") !== priorityFilter) return false;
  if (methodFilter !== "all") {
    if (methodFilter === "none" && o.execution_method != null) return false;
    if (methodFilter !== "none" && o.execution_method !== methodFilter) return false;
  }
  if (decisionFilter !== "all") {
    if (decisionFilter === "none" && o.cio_decision != null && o.cio_decision !== "") return false;
    if (decisionFilter !== "none" && o.cio_decision !== decisionFilter) return false;
  }
  if (marketableFilter === "marketable" && !isMarketableOrder(o)) return false;
  return true;
};

/** Last-order presence/warning check. A row "has a last order" iff at least one
 *  sub-strategy resolved an order (post tracker.created_ts). */
export const matchesLastOrderFilter = (o: PendingOrder, lastOrderFilter: LastOrderFilter): boolean => {
  if (lastOrderFilter === "all") return true;
  const hasLastOrder = Object.values(o.last_orders_by_strategy ?? {}).some((v) => v != null);
  if (lastOrderFilter === "none" && hasLastOrder) return false;
  if (lastOrderFilter === "has_warning" && !o.last_order_mismatch) return false;
  if (lastOrderFilter === "has_pending_codes" && !(o.pending_order_codes?.length && o.cio_decision === "continue")) return false;
  return true;
};

/** Centralized filter logic for strategy, search, category, and execution filters. */
export const matchesFilters = (
  o: PendingOrder,
  strategyFilter: StrategyFilter,
  searchTerm: string,
  selectedCategories: string[],
  orderTypeFilter: OrderTypeFilter = "all",
  priorityFilter: PriorityFilter = "all",
  methodFilter: MethodFilter = "all",
  decisionFilter: DecisionFilter = "all",
  lastOrderFilter: LastOrderFilter = "all",
  marketableFilter: MarketableFilter = "all",
) => {
  const q = searchTerm.toLowerCase().trim();
  const matchStrategy = matchesStrategyGroup(o, strategyFilter);
  const matchSearch =
    !q ||
    o.company_name?.toLowerCase().includes(q) ||
    o.company_symbol?.toLowerCase().includes(q);
  // "All Orders" (on by default) bypasses the category check for real tracker
  // rows so an active pending order never vanishes because its company category
  // is outside the selected chips (e.g. "D" or none at all). Recommendation
  // rows ignore the switch and always follow the letter/Invested chips.
  const letterCategories = selectedCategories.filter((cat) => cat !== ALL_ORDERS_CATEGORY);
  const showAllOrders = !o.is_oms_rec && selectedCategories.includes(ALL_ORDERS_CATEGORY);
  const matchCategory =
    showAllOrders ||
    letterCategories.length === 0 ||
    letterCategories.some((cat) => {
      // "I" (Invested): prefer the OMS-computed classification — the same source
      // Trade Suggestions uses, which excludes inherited (transferred-in) positions.
      // Fall back to holdings only for rows with no OMS entry, so existing
      // pending orders don't vanish from the default category view.
      if (cat === "I") return o.is_invested;
      return o.company_category != null && o.company_category === cat;
    });

  // Execution + decision filters apply only to real tracker rows.
  if (!o.is_oms_rec && !o.is_placeholder &&
      !matchesExecutionFilters(o, orderTypeFilter, priorityFilter, methodFilter, decisionFilter, marketableFilter)) {
    return false;
  }

  if (!o.is_placeholder && !matchesLastOrderFilter(o, lastOrderFilter)) return false;

  return matchStrategy && matchSearch && matchCategory;
};

/** Calculates 1.02 * CMP default price. */
export const calcDefaultPrice = (cmp: number | null | undefined): string =>
  cmp ? (cmp * 1.02).toFixed(2) : "";

/**
 * Whether a bulk-edit/bulk-create field's current draft value differs from its
 * value when edit mode was entered (or, for a create row, from its seeded default) —
 * drives the "you've changed this" highlight. Undrafted fields never read as dirty.
 */
export const isFieldDirty = <T extends Record<string, unknown>>(
  draft: Partial<T> | undefined,
  snapshot: Partial<T> | undefined,
  field: keyof T,
): boolean => {
  if (!draft || !snapshot) return false;
  const draftValue = draft[field];
  return draftValue !== undefined && draftValue !== snapshot[field];
};

// ── Previous CIO instructions ──────────────────────────────────────────────

/** Short "13 Aug" session label for a prev-instruction entry. */
export const formatSessionDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "—";
  const parsed = new Date(isoDate);
  if (isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

/**
 * Hover line for a past instruction, e.g. "CIO 2.50% · High". The CIO decision is
 * deliberately left out — by the time a row carries history the decision is either
 * cleared or closed out, so it adds nothing the date and direction do not. Returns ""
 * when the instruction carried neither figure, and the caller drops the line.
 */
export const formatPrevInstruction = (entry: PrevInstruction): string => {
  const parts: string[] = [];
  if (entry.cio_target_pct != null) parts.push(`CIO ${fmtNum(entry.cio_target_pct)}%`);
  if (entry.priority) parts.push(`${entry.priority.charAt(0).toUpperCase()}${entry.priority.slice(1)}`);
  return parts.join(" · ");
};

/**
 * Cell line for a past instruction: direction, CIO target % and the price it was to be
 * worked at, e.g. "BUY · 2.50% · ₹150.00" ("Market" when it carried no limit). Absent
 * parts are dropped rather than shown blank; the decision, priority, volume % and comment
 * stay in the hover detail.
 */
export const formatPrevInstructionCell = (entry: PrevInstruction, fmtPrice: PriceFmt): string => {
  const parts: string[] = [];
  if (entry.order_type === 1) parts.push("BUY");
  else if (entry.order_type === 2) parts.push("SELL");
  if (entry.cio_target_pct != null) parts.push(`${fmtNum(entry.cio_target_pct)}%`);
  if (entry.execution_type === "market") parts.push("Market");
  else if (entry.limit_price != null) parts.push(fmtPrice(entry.limit_price));
  return parts.join(" · ");
};

/** Execution leg of a past instruction, e.g. "Limit ₹150.00 · 5% vol"; "" when absent. */
export const formatPrevExecution = (entry: PrevInstruction, fmtPrice: PriceFmt): string => {
  const parts: string[] = [];
  if (entry.execution_type === "market") {
    parts.push("Market");
  } else if (entry.execution_type) {
    parts.push(entry.limit_price != null ? `Limit ${fmtPrice(entry.limit_price)}` : "Limit");
  }
  if (entry.execution_method === "vwap") parts.push("VWAP");
  else if (entry.execution_method === "pct_volume" && entry.volume_pct != null) {
    parts.push(`${fmtNum(entry.volume_pct, 0)}% vol`);
  }
  return parts.join(" · ");
};
